import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { PaButtonComponent } from '../pa-button/pa-button.component';
import { PaContextMenuComponent } from '../pa-context-menu/pa-context-menu.component';
import { PaBulkAction } from './models/pa-bulk-actions-bar.model';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
@Component({
  selector: 'pa-bulk-actions-bar',
  standalone: true,
  imports: [CommonModule, PaButtonComponent, PaContextMenuComponent],
  templateUrl: './pa-bulk-actions-bar.component.html',
  styleUrls: ['./pa-bulk-actions-bar.component.scss'],
})
export class PaBulkActionsBarComponent implements OnChanges, OnInit {
  /**
   * Контекст для аналитики (например, "salary_to_pay_list").
   * Если не задан, события не отправляются.
   */
  @Input() analyticsContext: string | null = null;
  /**
   * Количество выбранных строк
   */
  @Input() selectedCount: number = 0;
  /**
   * Общее количество строк
   */
  @Input() totalCount: number = 0;
  /**
   * Действия (массив действий)
   */
  @Input() actions: PaBulkAction[] = [];
  /**
   * Показывать ли кнопку "Выбрать все"
   */
  @Input() showSelectAll: boolean = false;
  /**
   * Текст для счетчика выбранных строк
   */
  @Input() selectedLabel: string = 'Выбрано -';
  /**
   * Показывать ли кнопку "Выбрать все" как активированную
   * когда выбраны все строки.
   */
  @Input() isAllSelected: boolean = false;

  /**
   * Событие закрытия панели
   * @returns void
   */
  @Output() close = new EventEmitter<void>();
  /**
   * Событие изменения выбора всех строк
   * @returns boolean
   */
  @Output() toggleSelectAll = new EventEmitter<boolean>();
  /**
   * Событие нажатия на действие
   * @returns PaBulkAction
   */
  @Output() actionClick = new EventEmitter<PaBulkAction>();

  isMobile: boolean = false;
  // Брейкпоинт, ниже которого панель ведет себя как мобильная.
  breakpoint: number = 1199;
  mobileMenuItems: MenuItem[] = [];
  // Флаг, который отслеживает, была ли панель массовых действий ранее видимой (для аналитики).
  private wasVisible = false;

  constructor(private readonly analytics: AnalyticsService) {}

  ngOnInit(): void {
    this.updateIsMobile();
    this.wasVisible = this.selectedCount > 0;
  }

  @HostListener('window:resize')
  handleResize(): void {
    this.updateIsMobile();
  }

  handleClose(): void {
    this.capture('Зарплата: Массовые действия закрыты', {
      Способ: 'крестик',
    });
    this.close.emit();
  }

  handleSelectAll(): void {
    const shouldSelect = !this.isAllSelected;
    this.capture('Зарплата: Массовые действия выбрали всё', {
      Выбрать: shouldSelect ? 'Да' : 'Нет',
    });
    this.toggleSelectAll.emit(shouldSelect);
  }

  handleActionClick(action: PaBulkAction): void {
    if (action.disabled && action.disabled()) {
      return;
    }
    this.capture('Зарплата: Массовые действия клик по действию', {
      Действие: action?.id ?? null,
      Название: action?.label ?? null,
    });
    if (action.command) {
      action.command();
    }
    this.actionClick.emit(action);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCount']) {
      const isVisible = this.selectedCount > 0;
      const becameVisible = isVisible && !this.wasVisible;
      const becameHidden = !isVisible && this.wasVisible;

      if (becameVisible) {
        this.capture('Зарплата: Массовые действия показаны');
      } else if (becameHidden) {
        this.capture('Зарплата: Массовые действия скрыты');
      }

      this.wasVisible = isVisible;
    }
    if (changes['actions']) {
      this.mobileMenuItems = (this.actions || [])
        .filter((action) => (action.visible ? action.visible() : true))
        .map<MenuItem>((action) => ({
          label: action.label,
          icon: action.icon,
          disabled: action.disabled ? action.disabled() : false,
          command: () => this.handleActionClick(action),
        }));
    }
  }

  get hasActions(): boolean {
    return this.actions && this.actions.length > 0;
  }

  private updateIsMobile(): void {
    this.isMobile = window.innerWidth <= this.breakpoint;
  }

  private capture(event: string, props?: Record<string, unknown>): void {
    const context = this.analyticsContext?.trim();
    if (!context) {
      return;
    }

    this.analytics.phCapture(event, {
      Раздел: context,
      Выбрано: this.selectedCount ?? 0,
      Всего: this.totalCount ?? 0,
      ...(props || {}),
    });
  }
}
